import * as FullStory from '@fullstory/browser';
import { fetchApiGetIntercomIdentityToken } from '@repo/api-codegen/v2';
import { analyticsPermissible } from '@repo/common/analytics';
import { useQuery } from '@tanstack/react-query';
import { useCallback } from 'react';
import { useAuthUser, useSuspenseIntegrationList } from '../api';
import { useLimits } from '../api/hooks/workspace/useLimits';
import { usePlan } from '../hooks/usePlans';
import { coalesceName } from './authorization/roles';

export const useFullStory = () => {
	const { user } = useAuthUser();

	const queryFn = async () => {
		if (analyticsPermissible(user)) {
			FullStory.init({ orgId: '12C2G2' }, () => {
				FullStory.identify(user.email, {
					displayName: coalesceName(user),
					email: user.email,
				});
			});
			return true;
		}
		return false;
	};

	return useQuery<boolean, boolean, void>({
		queryKey: ['fullstory'],
		queryFn,
	});
};

// Sentry disabled - no-op
export const useSentry = () =>
	useQuery<boolean, boolean, void>({
		queryKey: ['sentry'],
		queryFn: async () => false,
	});

export const useConfigureIntercom = () => {
	const { user, workspace } = useAuthUser();
	const { data } = useSuspenseIntegrationList({});
	const { daysLeftInTrial, trial, plan, paywall } = usePlan();
	const limitDetails = useLimits();

	const queryFn = useCallback(async () => {
		if (analyticsPermissible(user, workspace)) {
			// Fetch Intercom identity verification token to prevent user impersonation
			const { token: intercom_user_jwt } =
				await fetchApiGetIntercomIdentityToken({});

			// @ts-expect-error TS(2339): Property 'Intercom' does not exist on type 'Window... Remove this comment to see the full error message
			window.Intercom('boot', {
				app_id: 'dssmg53d',
				name: user.display_name,
				role: user.role,
				is_customer: !trial && !paywall,
				days_left_in_trial: daysLeftInTrial,
				trial: `${trial}`,
				plan: `${trial ? 'Trial' : plan?.title}`,
				editor_seats_used: `${limitDetails.usedEditorOrAdminSeats} of ${limitDetails.editorOrAdminSeats}`,
				viewer_seats_used: `${limitDetails.usedViewerSeats} of ${limitDetails.viewerSeats}`,
				number_of_integrations: data?.count,
				// Includes user_id, email, and workspace_id
				intercom_user_jwt,
			});
			return true;
		}
		return false;
	}, [
		user,
		workspace,
		trial,
		paywall,
		daysLeftInTrial,
		plan?.title,
		limitDetails.usedEditorOrAdminSeats,
		limitDetails.editorOrAdminSeats,
		limitDetails.usedViewerSeats,
		limitDetails.viewerSeats,
		data?.count,
	]);

	return useQuery<boolean, boolean, void>({
		queryKey: ['intercom'],
		queryFn,
	});
};
